import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { api } from "@/lib/api";

const HTML_CFG = { ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "ul", "ol", "li", "h3", "h4", "a", "blockquote"], ALLOWED_ATTR: ["href", "title", "rel"] };
const COLS = { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3" };
const STAT_COLS = { 1: "sm:grid-cols-1", 2: "sm:grid-cols-2", 3: "sm:grid-cols-3", 4: "sm:grid-cols-4" };

const Heading = ({ b, testid }) => (b.title || b.subtitle) ? (
  <div className="mb-4">
    {b.title && <h2 className="text-xl sm:text-2xl font-bold tracking-tight" data-testid={`${testid}-title`}>{b.title}</h2>}
    {b.subtitle && <p className="text-sm text-muted-foreground mt-1">{b.subtitle}</p>}
  </div>
) : null;

const FaqSection = ({ b }) => (
  <section className="mx-auto max-w-4xl px-4 py-8" data-testid={`home-faq-${b.id}`}>
    <Heading b={b} testid={`home-faq-${b.id}`} />
    <Accordion type="single" collapsible className="rounded-2xl border bg-card px-4">
      {b.items.map((it, i) => (
        <AccordionItem key={i} value={`q${i}`} className={i === b.items.length - 1 ? "border-b-0" : ""}>
          <AccordionTrigger className="text-left font-semibold" data-testid={`home-faq-${b.id}-q-${i}`}>{it.q}</AccordionTrigger>
          <AccordionContent className="text-muted-foreground whitespace-pre-line" data-testid={`home-faq-${b.id}-a-${i}`}>{it.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

const StatsSection = ({ b }) => (
  <section className="mx-auto max-w-6xl px-4 py-8" data-testid={`home-stats-${b.id}`}>
    <Heading b={b} testid={`home-stats-${b.id}`} />
    <div className={`grid grid-cols-2 ${STAT_COLS[Math.min(4, b.items.length)]} gap-3`}>
      {b.items.map((it, i) => (
        <div key={i} className="rounded-2xl bg-[hsl(var(--brand-black))] text-white p-5 border-l-4 border-primary" data-testid={`home-stat-${b.id}-${i}`}>
          <div className="text-3xl sm:text-4xl font-extrabold text-primary tracking-tight">{it.value}</div>
          <div className="text-sm text-white/70 mt-1">{it.label}</div>
        </div>
      ))}
    </div>
  </section>
);

const ColumnsSection = ({ b }) => (
  <section className="mx-auto max-w-6xl px-4 py-8" data-testid={`home-columns-${b.id}`}>
    <Heading b={b} testid={`home-columns-${b.id}`} />
    <div className={`grid grid-cols-1 ${COLS[b.columns.length]} gap-6`}>
      {b.columns.map((c, i) => (
        <div key={i} data-testid={`home-column-${b.id}-${i}`}
          className="text-sm leading-relaxed space-y-2 [&_h3]:text-lg [&_h3]:font-bold [&_h4]:font-semibold [&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-3 [&_blockquote]:italic"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.html, HTML_CFG) }} />
      ))}
    </div>
  </section>
);

const AdSection = ({ b, preview }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (preview || !ref.current || !("IntersectionObserver" in window)) { return undefined; }
    const ob = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { api.post(`/cms/ads/${b.id}/impression`).catch(() => {}); ob.disconnect(); }
    }, { threshold: 0.5 });
    ob.observe(ref.current);
    return () => ob.disconnect();
  }, [b.id, preview]);
  const click = () => { if (!preview) api.post(`/cms/ads/${b.id}/click`).catch(() => {}); };
  const img = <img src={b.image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${b.image_url}` : b.image_url} alt={b.alt_text} loading="lazy" className="w-full h-auto max-h-72 object-cover rounded-2xl" />;
  const cls = "block relative group";
  return (
    <section ref={ref} className="mx-auto max-w-6xl px-4 py-6" data-testid={`home-ad-${b.id}`}>
      <div className="relative">
        <span className="absolute top-2 left-2 z-10 text-[10px] uppercase tracking-wider font-bold bg-black/60 text-white px-2 py-0.5 rounded-full">Reklam</span>
        {!b.link_url ? img : b.link_url.startsWith("/")
          ? <Link to={b.link_url} onClick={click} className={cls} data-testid={`home-ad-link-${b.id}`}>{img}</Link>
          : <a href={b.link_url} target="_blank" rel="noopener noreferrer sponsored" onClick={click} className={cls} data-testid={`home-ad-link-${b.id}`}>{img}</a>}
      </div>
    </section>
  );
};

const RENDER = { faq: FaqSection, stats: StatsSection, columns: ColumnsSection, ad: AdSection };

export const HomeBlock = ({ b, preview }) => {
  const C = RENDER[b.type];
  return b.visible && C ? <C b={b} preview={preview} /> : null;
};

const setMeta = (attr, key, val, created) => {
  let m = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!m) { m = document.createElement("meta"); m.setAttribute(attr, key); document.head.appendChild(m); created.push(m); }
  const prev = m.getAttribute("content");
  m.setAttribute("content", val);
  return () => { if (prev !== null) m.setAttribute("content", prev); };
};

export const useHomeSeo = (seo, blocks) => {
  useEffect(() => {
    const created = [];
    const undo = [];
    const prevTitle = document.title;
    const absImg = seo.og_image_url && (seo.og_image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${seo.og_image_url}` : seo.og_image_url);
    if (seo.title) { document.title = seo.title; undo.push(setMeta("property", "og:title", seo.title, created)); }
    if (seo.description) {
      undo.push(setMeta("name", "description", seo.description, created));
      undo.push(setMeta("property", "og:description", seo.description, created));
    }
    if (absImg) undo.push(setMeta("property", "og:image", absImg, created));
    const faqs = blocks.filter((b) => b.type === "faq" && b.visible).flatMap((b) => b.items);
    if (faqs.length) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = "home-faq-jsonld";
      s.text = JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) });
      document.head.appendChild(s);
      created.push(s);
    }
    return () => { document.title = prevTitle; undo.forEach((f) => f()); created.forEach((n) => n.remove()); };
  }, [seo, blocks]);
};
